import React, { useState } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Gift, Sparkles, Send, Link2, MessageCircle, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable Refer-a-Friend dialog. Drop it anywhere with <ReferDialog trigger={<Button>...</Button>} />.
 * The referrer is automatically the logged-in user (server reads JWT.sub and tags referrer_type from role).
 */
export default function ReferDialog({ trigger, defaultBranchId, onSuccess }) {
  const { user, activeBranchId } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    referral_name: "", referral_phone: "", referral_email: "",
    branch_id: defaultBranchId || activeBranchId || (user?.branch_ids?.[0] ?? ""),
    notes: "",
  });

  const { data: branches = [] } = useQuery({
    queryKey: ["branches"],
    queryFn: async () => (await apiClient.get("/branches")).data,
    enabled: open,
  });

  const create = useMutation({
    mutationFn: async (body) => (await apiClient.post("/referrals", body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["referrals"] });
      qc.invalidateQueries({ queryKey: ["my-referrals"] });
      qc.invalidateQueries({ queryKey: ["leads"] });
      toast.success("Referral submitted — our team will reach out.");
      setOpen(false);
      setForm((f) => ({ ...f, referral_name: "", referral_phone: "", referral_email: "", notes: "" }));
      onSuccess?.();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to submit"),
  });

  const submit = () => {
    if (!form.referral_name || !form.referral_phone) {
      toast.error("Name & phone required");
      return;
    }
    if (!form.branch_id) {
      toast.error("Pick a branch");
      return;
    }
    create.mutate(form);
  };

  const reward = user?.role === "COACH"
    ? "REPS bonus on conversion (configurable rate)"
    : "REPS on conversion (configurable rate)";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#0a0a0a] border-zinc-800 text-white max-w-md" data-testid="refer-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight flex items-center gap-2">
            <Gift size={20} className="text-volt" /> Refer a Friend
          </DialogTitle>
        </DialogHeader>
        <div className="bg-volt/10 border border-volt/30 px-3 py-2 rounded-sm flex items-center gap-2 text-xs text-volt">
          <Sparkles size={14} /> When they sign up: <span className="font-bold">{reward}</span>
        </div>

        <ShareLinkBlock userId={user?.id} />

        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-600 text-center">— or fill it in for them —</div>

        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Friend's name *</Label>
            <Input data-testid="refer-name" value={form.referral_name} onChange={(e) => setForm({ ...form, referral_name: e.target.value })}
              className="bg-zinc-950 border-zinc-800 mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Friend's phone *</Label>
            <Input data-testid="refer-phone" value={form.referral_phone} onChange={(e) => setForm({ ...form, referral_phone: e.target.value })}
              placeholder="+91 9..." className="bg-zinc-950 border-zinc-800 mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Friend's email</Label>
            <Input data-testid="refer-email" value={form.referral_email} onChange={(e) => setForm({ ...form, referral_email: e.target.value })}
              className="bg-zinc-950 border-zinc-800 mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Branch</Label>
            <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1" data-testid="refer-branch"><SelectValue placeholder="Choose branch" /></SelectTrigger>
              <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Note (optional)</Label>
            <Textarea data-testid="refer-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2} placeholder="What are they looking for?" className="bg-zinc-950 border-zinc-800 mt-1" />
          </div>
          <Button onClick={submit} disabled={create.isPending} data-testid="refer-submit"
            className="w-full bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm">
            <Send size={14} className="mr-2" /> {create.isPending ? "Sending…" : "Submit Referral"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ShareLinkBlock({ userId }) {
  const [copied, setCopied] = React.useState(false);
  if (!userId) return null;
  const url = `${window.location.origin}/r/${userId}`;
  const message = `Come train at NammaXFit — my referral link gets us both hooked up: ${url}`;
  const waHref = `https://wa.me/?text=${encodeURIComponent(message)}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — long-press to select");
    }
  };
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-sm p-3 space-y-2" data-testid="share-link-block">
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Your Shareable Link</div>
      <div className="bg-black border border-zinc-800 rounded-sm px-2 py-1.5 text-[11px] font-mono text-zinc-300 truncate" data-testid="share-link-url">
        {url}
      </div>
      <div className="flex gap-2">
        <Button onClick={copy} data-testid="copy-link-button" size="sm" variant="outline"
          className="flex-1 bg-zinc-900 border-zinc-800 text-zinc-200 hover:border-volt/50 rounded-sm text-[11px] uppercase tracking-[0.14em]">
          {copied ? <><Check size={12} className="mr-1" /> Copied</> : <><Link2 size={12} className="mr-1" /> Copy Link</>}
        </Button>
        <a href={waHref} target="_blank" rel="noreferrer" className="flex-1" data-testid="whatsapp-share-button">
          <Button size="sm" className="w-full bg-[#25D366] hover:bg-[#20BC5B] text-black rounded-sm text-[11px] uppercase tracking-[0.14em]">
            <MessageCircle size={12} className="mr-1" /> WhatsApp
          </Button>
        </a>
      </div>
    </div>
  );
}
